import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PricingTabs } from "@/components/PricingTabs";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { type PricingPlan } from "@/utils/pricing";
import { Loader2, CreditCard, Ticket, ShieldCheck } from "lucide-react";

const cardDetailsSchema = z.object({
  cardHolderName: z.string().min(2, "Kart sahibinin adını giriniz"),
  cardNumber: z.string().min(16, "16 haneli kart numarasını giriniz").max(16).regex(/^\d+$/, "Sadece rakam giriniz"),
  cardExpiryMonth: z.string().min(2).max(2).regex(/^(0[1-9]|1[0-2])$/, "AA (01-12)"),
  cardExpiryYear: z.string().length(2, "YY (Örn: 26)").regex(/^\d+$/),
  cardCvv: z.string().min(3, "3 veya 4 haneli CVV giriniz").max(4).regex(/^\d+$/),
  couponCode: z.string().optional(),
});

interface PaymentFormProps {
  userName: string;
  userDetails: any;
  selectedCoach: any;
  plans: PricingPlan[];
  selectedPlan: PricingPlan | null;
  onSelectPlan: (plan: PricingPlan) => void;
}

export const PaymentForm = ({
  userName,
  userDetails,
  selectedCoach,
  plans,
  selectedPlan,
  onSelectPlan
}: PaymentFormProps) => {
  const { toast } = useToast();
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount_amount: number;
  } | null>(null);

  // 🔥 ANLIK HESAPLAMA: Sekme her değiştiğinde bu değişken otomatik güncellenir.
  const amountToPay = selectedPlan
    ? Math.max(0, selectedPlan.totalPrice - (appliedCoupon?.discount_amount || 0))
    : 0;

  const cardForm = useForm({
    resolver: zodResolver(cardDetailsSchema),
    defaultValues: {
      cardHolderName: "",
      cardNumber: "",
      cardExpiryMonth: "",
      cardExpiryYear: "",
      cardCvv: "",
      couponCode: "",
    },
  });

  const validateCoupon = async (code: string) => {
    if (!code) {
      setAppliedCoupon(null);
      return;
    }
    setIsValidatingCoupon(true);
    try {
      const { data: coupon, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', code.trim().toUpperCase())
        .eq('active', true)
        .single();

      if (error || !coupon) {
        toast({ variant: "destructive", title: "Kupon Geçersiz", description: "Lütfen kodu kontrol edin." });
        setAppliedCoupon(null);
        return;
      }
      setAppliedCoupon(coupon);
      toast({ title: "Kupon Uygulandı!", description: `${coupon.discount_amount}₺ indirim yapıldı.` });
    } catch (error) {
      console.error(error);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const onSubmit = async (data: z.infer<typeof cardDetailsSchema>) => {
    try {
      setIsSubmitting(true);
      if (!selectedPlan) throw new Error('Lütfen bir plan seçiniz');

      // 🔥 GÜVENLİK: Butona basıldığı andaki tutarı tekrar hesaplıyoruz
      const finalCheckAmount = selectedPlan.totalPrice - (appliedCoupon?.discount_amount || 0);

      console.log("PAYTR GİDEN TUTAR:", finalCheckAmount);

      const paymentRequest = {
        email: userDetails.email,
        nameSurname: userDetails.nameSurname,
        phone: userDetails.phone,
        address: userDetails.address || "İstanbul/Türkiye",
        amount: finalCheckAmount,
        userBasket: [{
          name: `${selectedCoach.name_surname} - ${selectedPlan.name}`,
          price: finalCheckAmount,
          quantity: 1
        }],
        ccOwner: data.cardHolderName,
        cardNumber: data.cardNumber,
        expiryMonth: data.cardExpiryMonth,
        expiryYear: data.cardExpiryYear,
        cvv: data.cardCvv,
        coachId: selectedCoach.id,
        couponCode: appliedCoupon?.code
      };

      const { data: paymentResponse, error } = await supabase.functions.invoke('create-payment', {
        body: paymentRequest
      });

      if (error) throw error;
      if (paymentResponse?.html) {
        document.open();
        document.write(paymentResponse.html);
        document.close();
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: "Ödeme Hatası", description: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="lg:col-span-1 bg-slate-900/40 border-white/10 backdrop-blur-md text-white border-none shadow-2xl overflow-hidden">
      <CardHeader className="bg-[#4F46E5]/10 border-b border-white/5 pb-6">
        <CardTitle className="text-white text-2xl">
          Merhaba <span className="text-[#4F46E5] font-black">{userName}</span>,
          <span className="block text-base font-light text-slate-400 mt-1">Sipariş özetini ve kart bilgilerini tamamla</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-8 pt-6">

        {/* Plan Seçimi (PricingTabs artık kendi orijinal renklerinde gelecek) */}
        <div className="space-y-3">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Plan Seçimi</label>
          <PricingTabs
            plans={plans}
            selectedPlan={selectedPlan}
            onSelectPlan={onSelectPlan}
            showActionButton={false}
          />
        </div>

        {/* Dinamik Tutar Alanı */}
        <div className="bg-[#4F46E5]/20 p-6 rounded-2xl border border-[#4F46E5]/40 shadow-inner group transition-all">
          <div className="flex justify-between items-center">
            <span className="text-slate-200 font-semibold italic text-lg tracking-tight">Ödenecek Toplam:</span>
            <div className="text-right">
              {appliedCoupon ? (
                <div className="flex flex-col">
                  <span className="line-through text-slate-500 text-sm font-bold">{selectedPlan?.totalPrice}₺</span>
                  <span className="text-4xl font-black text-emerald-400 tracking-tighter animate-in zoom-in-95 duration-300">{amountToPay}₺</span>
                </div>
              ) : (
                <span className="text-4xl font-black text-white tracking-tighter">{amountToPay}₺</span>
              )}
            </div>
          </div>
        </div>

        <Form {...cardForm}>
          <form onSubmit={cardForm.handleSubmit(onSubmit)} className="space-y-6">

            {/* Kupon Alanı */}
            <div className="grid gap-3">
              <div className="flex items-center gap-2 text-slate-400 px-1">
                <Ticket className="h-4 w-4" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em]">İndirim Kuponu</span>
              </div>
              <div className="flex gap-2">
                <FormField
                  control={cardForm.control}
                  name="couponCode"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input
                          placeholder="KUPON KODUNU GİR"
                          className="bg-white/5 border-white/10 text-white focus:border-[#4F46E5] h-11"
                          {...field}
                          disabled={isValidatingCoupon}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 border-white/10 hover:bg-[#4F46E5] text-white transition-colors px-6"
                  disabled={isValidatingCoupon}
                  onClick={() => validateCoupon(cardForm.getValues("couponCode"))}
                >
                  {isValidatingCoupon ? <Loader2 className="animate-spin h-4 w-4" /> : "UYGULA"}
                </Button>
              </div>
            </div>

            <div className="space-y-5 bg-white/5 p-5 rounded-2xl border border-white/5 relative">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <CreditCard className="h-4 w-4" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em]">Kart Bilgileri</span>
              </div>

              <FormField control={cardForm.control} name="cardHolderName" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-500 text-[10px] font-bold uppercase">AD SOYAD</FormLabel>
                  <FormControl><Input placeholder="KARTIN ÜSTÜNDEKİ İSİM" className="bg-slate-950/40 border-white/5 text-white" {...field} /></FormControl>
                  <FormMessage className="text-rose-400" />
                </FormItem>
              )} />

              <FormField control={cardForm.control} name="cardNumber" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-500 text-[10px] font-bold uppercase">KART NUMARASI</FormLabel>
                  <FormControl><Input placeholder="0000 0000 0000 0000" maxLength={16} className="bg-slate-950/40 border-white/5 text-white tracking-[0.2em] font-mono" {...field} /></FormControl>
                  <FormMessage className="text-rose-400" />
                </FormItem>
              )} />

              <div className="grid grid-cols-3 gap-4">
                <FormField control={cardForm.control} name="cardExpiryMonth" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 text-[10px] font-bold uppercase">AY</FormLabel>
                    <FormControl><Input placeholder="AA" maxLength={2} className="bg-slate-950/40 border-white/5 text-white text-center" {...field} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={cardForm.control} name="cardExpiryYear" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 text-[10px] font-bold uppercase">YIL</FormLabel>
                    <FormControl><Input placeholder="YY" maxLength={2} className="bg-slate-950/40 border-white/5 text-white text-center" {...field} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={cardForm.control} name="cardCvv" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 text-[10px] font-bold uppercase">CVV</FormLabel>
                    <FormControl><Input type="password" placeholder="***" maxLength={4} className="bg-slate-950/40 border-white/5 text-white text-center" {...field} /></FormControl>
                  </FormItem>
                )} />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white h-16 text-xl font-black shadow-2xl shadow-indigo-600/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-3">
                  <Loader2 className="animate-spin h-6 w-6" /> İŞLEM YAPILIYOR...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-6 w-6" /> ÖDEMEYİ GÜVENLE TAMAMLA
                </div>
              )}
            </Button>

            <p className="text-center text-[9px] text-slate-600 font-bold tracking-[0.3em] uppercase">
              256-Bit SSL Secured Payment
            </p>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};